import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  messagesService,
  type MessageListParams,
  type CreateMessageData,
  type UpdateMessageData,
} from '../services/api/messages'
import type { Message } from '../types'

// Clés stables pour les queries
export const msgKeys = {
  conversations: (userId: string) => ['messages', 'conversations', userId] as const,
  conversation: (userId1: string, userId2: string) =>
    ['messages', 'conversation', userId1, userId2] as const,
  unread: (userId: string) => ['messages', 'unread', userId] as const,
}

export function useMessages(params?: MessageListParams) {
  return useQuery({
    queryKey: ['messages', params],
    queryFn: async () => {
      const result = await messagesService.list(params)
      if (!result.success) throw new Error(result.message ?? 'Impossible de récupérer les messages')
      return result.data
    },
    staleTime: 30_000,
  })
}

export function useConversation(userId1: string, userId2: string, params?: Omit<MessageListParams, 'conversationId'>) {
  return useQuery({
    queryKey: msgKeys.conversation(userId1, userId2),
    queryFn: async () => {
      const result = await messagesService.getConversation(userId1, userId2, params)
      if (!result.success) throw new Error(result.message ?? 'Impossible de récupérer la conversation')
      return result.data ?? []
    },
    enabled: !!userId1 && !!userId2,
    staleTime: 10_000,
  })
}

export function useConversations(userId: string) {
  return useQuery({
    queryKey: msgKeys.conversations(userId),
    queryFn: async () => {
      const result = await messagesService.getConversations(userId)
      if (!result.success) throw new Error(result.message ?? 'Impossible de récupérer les conversations')
      return result.data ?? []
    },
    enabled: !!userId,
    staleTime: 30_000,
  })
}

export function useCreateMessage(userId1: string, userId2: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateMessageData) => {
      const result = await messagesService.create(payload)
      if (!result.success) throw new Error(result.message ?? 'Création du message impossible')
      return result.data!
    },
    // Optimistic: ajoute immédiatement le message dans le cache
    onMutate: async (payload) => {
      const key = msgKeys.conversation(userId1, userId2)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Message[]>(key)
      const optimistic: Message = {
        id: -Date.now(), // id temporaire négatif
        senderId: Number(userId1),
        receiverId: Number(userId2),
        conversationId: '',
        content: payload.content,
        status: 'sent',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sender: { id: Number(userId1), firstName: '', lastName: '' },
        receiver: { id: Number(userId2), firstName: '', lastName: '' },
      }
      queryClient.setQueryData<Message[]>(key, old => [...(old ?? []), optimistic])
      return { previous, key }
    },
    onSuccess: (newMsg, _vars, ctx) => {
      if (!ctx) return
      // Remplace le dernier message optimiste (id < 0) par le vrai message
      queryClient.setQueryData<Message[]>(ctx.key, old => {
        if (!old) return [newMsg]
        // Trouve le message optimiste à remplacer (même contenu, id négatif)
        const idx = [...old].reverse().findIndex(m => m.id < 0 && m.content === newMsg.content)
        if (idx === -1) {
          // Pas trouvé : ajoute seulement si pas déjà présent
          return old.some(m => m.id === newMsg.id) ? old : [...old, newMsg]
        }
        const realIdx = old.length - 1 - idx
        return old.map((m, i) => i === realIdx ? newMsg : m)
      })
      // Rafraîchit la liste des conversations (lastMessage)
      void queryClient.invalidateQueries({ queryKey: msgKeys.conversations(userId1) })
    },
    onError: (_err, _vars, ctx) => {
      // Rollback optimiste
      if (ctx) queryClient.setQueryData<Message[]>(ctx.key, ctx.previous)
      toast.error('Erreur lors de l\'envoi du message')
    },
  })
}

export function useDeleteMessage(userId1: string, userId2: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await messagesService.delete(id)
      if (!result.success) throw new Error(result.message ?? 'Suppression du message impossible')
      return id
    },
    onSuccess: (id) => {
      const key = msgKeys.conversation(userId1, userId2)
      queryClient.setQueryData<Message[]>(key, old => old?.filter(m => m.id !== id))
      void queryClient.invalidateQueries({ queryKey: msgKeys.conversations(userId1) })
      toast.success('Message supprimé')
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : "Une erreur s'est produite")
    },
  })
}

export function useUpdateMessage(userId1: string, userId2: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateMessageData }) => {
      const result = await messagesService.update(id, data)
      if (!result.success) throw new Error(result.message ?? 'Mise à jour du message impossible')
      return result.data!
    },
    onSuccess: (updated) => {
      const key = msgKeys.conversation(userId1, userId2)
      queryClient.setQueryData<Message[]>(key, old =>
        old?.map(m => m.id === updated.id ? updated : m)
      )
      toast.success('Message modifié')
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : "Une erreur s'est produite")
    },
  })
}

export function useMarkConversationAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      conversationId,
    }: {
      conversationId: string
      userId: string
    }) => {
      const result = await messagesService.markConversationAsRead(conversationId)
      if (!result.success) {
        throw new Error(result.message ?? 'Marquage de la conversation impossible')
      }
      return result.data
    },
    onSuccess: (_data, { userId }) => {
      void queryClient.invalidateQueries({ queryKey: msgKeys.conversations(userId) })
      void queryClient.invalidateQueries({ queryKey: msgKeys.unread(userId) })
    },
  })
}

export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, messageIds }: { userId: string; messageIds: number[] }) => {
      const result = await messagesService.markAsRead(userId, messageIds)
      if (!result.success) throw new Error(result.message ?? 'Marquage des messages impossible')
      return result.data
    },
    onSuccess: (_data, { userId }) => {
      void queryClient.invalidateQueries({ queryKey: msgKeys.conversations(userId) })
      void queryClient.invalidateQueries({ queryKey: msgKeys.unread(userId) })
    },
  })
}

export function useUnreadCount(userId: string) {
  return useQuery({
    queryKey: msgKeys.unread(userId),
    queryFn: async () => {
      const result = await messagesService.getUnreadCount(userId)
      if (!result.success) throw new Error(result.message ?? 'Impossible de récupérer le nombre de messages non lus')
      return result.data?.count ?? 0
    },
    enabled: !!userId,
    staleTime: 10_000,
    refetchInterval: 15_000,
  })
}
